import Store from '@/store';

const LOGIN = {
  name: 'Login',
  path: '/login',
  component: () => import(/* webpackChunkName: "Login" */ '@/pages/Login'),
  meta: {
    requiresAuth: false,
    history: false,
  },
};

const AUTH = {
  name: 'Auth',
  path: '/auth-return',
  component: () => import(/* webpackChunkName: "Auth" */ '@/pages/Auth'),
  meta: {
    requiresAuth: false,
  },
};

const LOGOUT = {
  name: 'Logout',
  path: '/logout',
  beforeEnter: (to, from, next) => {
    const logoutFirebase = Store.dispatch('AuthFirebase/signOut');
    const logoutAuth = Store.dispatch('Auth/logout');

    Promise.all([logoutFirebase, logoutAuth])
      .then(() => next({ name: 'Login' }));
  },
  meta: {
    requiresAuth: true,
  },
};

const COURSES = {
  name: 'courses',
  path: '/courses',
  alias: ['/', '/dashboard'],
  component: () => import(/* webpackChunkName: "Courses" */ '@/pages/Courses'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const SURVEYS = {
  name: 'Surveys',
  path: '/surveys',
  component: () => import(/* webpackChunkName: "Courses" */ '@/pages/Surveys'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const SURVEY_TEACHER_COURSE_CALENDAR = {
  name: 'SurveyTeacherCourseCalendar',
  path: '/survey/:curso_id/:calendario_id',
  props: true,
  component: () => import(/* webpackChunkName: "Courses" */ '@/pages/survey/Course'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const SURVEY_TEACHER_COURSE_CALENDAR_DETAIL = {
  name: 'SurveyTeacherCourseCalendarDetail',
  path: '/survey/:curso_id/:calendario_id/detail',
  props: true,
  component: () => import(/* webpackChunkName: "Courses" */ '@/pages/survey/CourseCalendarDetail'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const SURVEY_COURSE_CALENDAR = {
  name: 'surveyCourseCalendar',
  path: '/course/:courseCode/calendar/:calendarId/survey',
  props: true,
  component: () => import(/* webpackChunkName: "CourseCalendarSurvey" */ '@/pages/course/calendar/Survey'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const COURSE_CALENDAR_RECORDINGS = {
  name: 'CourseCalendarRecordings',
  path: '/course/:curso_id/calendar/:calendario_id/recordings',
  props: true,
  component: () => import(/* webpackChunkName: "CourseCalendarRecordings" */ '@/pages/course/calendar/Recordings'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const COURSE = {
  name: 'course',
  path: '/course/:courseCode/:calendarId/:wall?/:highlight?',
  props: true,
  component: () => import(/* webpackChunkName: "Course" */ '@/pages/Course'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const COURSE_ENROLLMENT = {
  name: 'courseEnrollment',
  path: '/enrollment/course/:preSelectedCourse?',
  props: true,
  component: () => import(/* webpackChunkName: "Enrollment" */ '@/pages/course/enrollment'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const EXAM_LANDING = {
  name: 'examLanding',
  path: '/exams/course/:courseCode/:calendarId',
  props: true,
  component: () => import(/* webpackChunkName: "ExamLanding" */ '@/pages/exam/Landing'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const EXAM_STARTED = {
  name: 'examStarted',
  path: '/course/:courseCode/exam/:exam/:calendarId',
  props: true,
  component: () => import(/* webpackChunkName: "ExamStarted" */ '@/pages/exam/Started'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const EXAM_RESULT = {
  name: 'examResult',
  path: '/course/:courseCode/exam/:examId/attempt/:attemptId/:animate?/:calendarId?',
  props: true,
  component: () => import(/* webpackChunkName: "ExamResult" */ '@/pages/exam/Result'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const CLASS = {
  name: 'class',
  path: '/course/:courseCode/class/:classId?/:calendarId?',
  props: true,
  component: () => import(/* webpackChunkName: "Class" */ '@/pages/class'),
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const CONTACT = {
  name: 'contact',
  path: '/contact/department',
  props: true,
  component: () => import(/* webpackChunkName: "Contact" */ '@/pages/Contact'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const BENEFITS = {
  name: 'Benefits',
  path: '/benefits',
  props: true,
  component: () => import(/* webpackChunkName: "Benefits" */ '@/pages/Benefits'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const PROFILE = {
  name: 'Profile',
  path: '/profile',
  props: true,
  component: () => import(/* webpackChunkName: "Profile" */ '@/pages/Profile'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const COMMUNITIES = {
  name: 'Communities',
  path: '/communities',
  redirect: { name: 'Discord' }
};

const DISCORD = {
  name: 'Discord',
  path: '/discord',
  component: () => import(/* webpackChunkName: "Communities" */ '@/pages/Discord'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const CREDITS = {
  name: 'Credits',
  path: '/credits',
  component: () => import(/* webpackChunkName: "Credits" */ '@/pages/Credits'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const ACHIEVEMENT_LANDING = {
  name: 'Roles',
  path: '/roles',
  component: () => import(/* webpackChunkName: "Achievements" */ '@/pages/Roles'),
  meta: {
    layout: 'DefaultLayout',
    requiresAuth: true,
    history: true,
  },
};

const ACHIEVEMENT_CAREER = {
  name: 'careerRoles',
  path: '/roles/career/:career',
  component: () => import(/* webpackChunkName: "Achievements" */ '@/pages/achievement/CareerAchievement'),
  props: true,
  meta: {
    requiresAuth: true,
    history: true,
  },
};

const MAINTENANCE = {
  name: 'maintenance',
  path: '/maintenance',
  props: true,
  component: () => import(/* webpackChunkName: "Maintenance" */ '@/pages/Maintenance'),
  meta: {
    requiresAuth: false,
    history: false,
  },
};
const ERROR = {
  name: 'error',
  path: '*',
  props: true,
  component: () => import(/* webpackChunkName: "Error" */ '@/pages/Error'),
  meta: {
    requiresAuth: false,
    history: false,
  },
};

export default [
  //HOME,
  LOGIN,
  AUTH,
  LOGOUT,
  BENEFITS,

  //DASHBOARD,
  SURVEYS,
  SURVEY_TEACHER_COURSE_CALENDAR,
  SURVEY_TEACHER_COURSE_CALENDAR_DETAIL,
  SURVEY_COURSE_CALENDAR,
  COURSE_CALENDAR_RECORDINGS,
  COURSES,
  EXAM_LANDING,
  EXAM_RESULT,
  EXAM_STARTED,
  CLASS,
  CONTACT,
  COURSE,
  COURSE_ENROLLMENT,

  PROFILE,

  COMMUNITIES,
  CREDITS,
  ACHIEVEMENT_LANDING,
  ACHIEVEMENT_CAREER,

  MAINTENANCE,
  ERROR,
  DISCORD
];
